import type { MyProfileModel } from '@/features/authentication/_models/me';
import { authenticationQueryKeyMap } from '@/features/authentication/_utils/query-keys';
import { useQueryClient } from '@/lib/react-query';
import { useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import type { z } from 'zod';
import {
  type FavoriteItemSchema,
  type FavoritesSchema,
  type FeatureFlagsLocalStorageSchema,
  devToolsLocalStorageKey,
} from '../_models/dev-tools';

export const useFeatureFlagsLocalStorage = () => {
  const [storageFeatureFlags, setStorageFeatureFlags] = useLocalStorageState<
    z.infer<typeof FeatureFlagsLocalStorageSchema>
  >(devToolsLocalStorageKey.Values['feature-flags']);

  const queryClient = useQueryClient();

  const me = queryClient.getQueryData<z.infer<typeof MyProfileModel>>(
    authenticationQueryKeyMap.me(),
  );

  const myFeatureFlags = me?.id ? storageFeatureFlags?.[me.id] : undefined;

  return { storageFeatureFlags, setStorageFeatureFlags, myFeatureFlags };
};

type DevtoolState =
  | {
      isDebugMode?: boolean;
    }
  | undefined;

const devtoolStateKey = 'dev-tools';

export const useDevToolsStorage = () => {
  const [devtoolState, setDevtoolState] =
    useLocalStorageState<DevtoolState>(devtoolStateKey);

  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<z.infer<typeof MyProfileModel>>(
    authenticationQueryKeyMap.me(),
  );
  const userId = me?.id ?? '';

  const loginUserSetting = devtoolState?.[userId] as DevtoolState;

  return {
    devtoolState: loginUserSetting,
    setDevtoolState: (values: DevtoolState) => {
      setDevtoolState((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    },
  };
};

export const useFavoriteLocalStorage = () => {
  const [favorites, setFavorites] = useLocalStorageState<
    z.infer<typeof FavoritesSchema>
  >(devToolsLocalStorageKey.Values.favorite, {
    defaultValue: [],
  });

  const addFavorite = useCallback(
    (favorite: Omit<z.infer<typeof FavoriteItemSchema>, 'id'>) => {
      setFavorites((prev) => [
        ...prev,
        {
          ...favorite,
          id: crypto.randomUUID(),
        },
      ]);
    },
    [setFavorites],
  );

  const updateFavorite = useCallback(
    (id: string, favorite: Omit<z.infer<typeof FavoriteItemSchema>, 'id'>) => {
      setFavorites((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...favorite,
                id,
              }
            : item,
        ),
      );
    },
    [setFavorites],
  );

  const removeFavorite = useCallback(
    (id: string) => {
      setFavorites((prev) => prev.filter((item) => item.id !== id));
    },
    [setFavorites],
  );

  return {
    favorites,
    addFavorite,
    updateFavorite,
    removeFavorite,
  };
};
